// src/components/features/campaigns/CampaignContentTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondary,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import DataStateHandler from '../../components/core/data/DataStateHandler';
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Publish as PublishIcon,
  Description as DraftIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Article as ArticleIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Poll as PollIcon,
} from '@mui/icons-material';

// Import services and stores
import * as campaignService from '../../services/campaignService';
import * as contentService from '../../services/contentService';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';

// Helper function to get content type icon
const getContentTypeIcon = (type) => {
  switch (type) {
    case 'image':
      return <ImageIcon />;
    case 'video':
      return <VideoIcon />;
    case 'poll':
      return <PollIcon />;
    default:
      return <ArticleIcon />;
  }
};

// Helper function to get status info
const getStatusInfo = (status) => {
  switch (status) {
    case 'draft':
      return { label: 'Draft', color: 'default', icon: <DraftIcon /> };
    case 'scheduled':
      return { label: 'Scheduled', color: 'warning', icon: <ScheduleIcon /> };
    case 'published':
      return { label: 'Published', color: 'success', icon: <PublishIcon /> };
    case 'cancelled':
      return { label: 'Cancelled', color: 'error', icon: <CancelIcon /> };
    default:
      return { label: 'Unknown', color: 'default', icon: <DraftIcon /> };
  }
};

// Helper function to format date
const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// CampaignContentTab component
const CampaignContentTab = ({ campaign }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedContentId, setSelectedContentId] = useState(null);
  
  // Hooks
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Check permissions
  const canCreate = hasPermission('content', 'create');
  const canUpdate = hasPermission('content', 'update');
  const canDelete = hasPermission('content', 'delete');
  
  // Fetch campaign content
  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      try {
        const response = await campaignService.getCampaignContent(campaign?._id);
        setContents(response.data.content);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch campaign content');
        showError(err.message || 'Failed to fetch campaign content');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchContent();
  }, [campaign?._id, showError]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle menu open
  const handleMenuOpen = (event, contentId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedContentId(contentId);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedContentId(null);
  };
  
  // Handle view content
  const handleViewContent = () => {
    navigate(`/content/${selectedContentId}`);
    handleMenuClose();
  };
  
  // Handle edit content
  const handleEditContent = () => {
    navigate(`/content/${selectedContentId}/edit`);
    handleMenuClose();
  };
  
  // Handle delete content
  const handleDeleteContent = async () => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      handleMenuClose();
      return;
    }
    
    try {
      await contentService.deleteContent(selectedContentId);
      showSuccess('Content deleted successfully');
      // Update local state
      setContents(contents.filter(content => content?._id !== selectedContentId));
    } catch (err) {
      showError(err.message || 'Failed to delete content');
    }
    
    handleMenuClose();
  };
  
  // Filter content by status for tabs
  const filterContentByStatus = (status) => {
    if (status === 'all') return contents;
    return contents.filter(content => {
      if (status === 'published+scheduled') {
        return content.status === 'published' || content.status === 'scheduled';
      }
      return content.status === status;
    });
  };
  
  // Create content button
  const handleCreateContent = () => {
    navigate(`/content/create?campaignId=${campaign?._id}`);
  };

  // Helper function to render content list
  const renderContentList = (contentList) => {
    if (contentList.length === 0) {
      return (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`
        }}>
          <Typography color="textSecondary" gutterBottom>
            No content found
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Create your first content piece for this campaign
          </Typography>
        </Box>
      );
    }
    
    return (
      <List>
        {contentList.map((content, index) => {
          const statusInfo = getStatusInfo(content.status);
          
          return (
            <React.Fragment key={content?._id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={(e) => handleMenuOpen(e, content?._id)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: statusInfo.color,
                      width: 40, 
                      height: 40 
                    }}
                  >
                    {statusInfo.icon}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="h6" 
                        component="span"
                        sx={{ fontSize: '1rem', fontWeight: 600 }}
                      >
                        {content.title || 'Untitled Content'}
                      </Typography>
                      <Chip 
                        label={statusInfo.label} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Typography
                        sx={{ display: 'inline' }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {content.text ? 
                          (content.text.length > 100 ? 
                            `${content.text.substring(0, 100)}...` : 
                            content.text) : 
                          'No description'}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {content.distributions && content.distributions.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {content.distributions.map((dist, idx) => (
                              <Chip 
                                key={idx} 
                                label={dist.platform?.charAt(0)?.toUpperCase() + dist.platform?.slice(1)} 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          {content.scheduledTime ? 
                            `Scheduled for: ${formatDate(content.scheduledTime)}` : 
                            content.publishedTime ? 
                              `Published on: ${formatDate(content.publishedTime)}` : 
                              `Created on: ${formatDate(content.createdAt)}`}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    );
  };
  
  return (
    <DataStateHandler
      isLoading={isLoading}
      error={error}
      isEmpty={contents.length === 0}
      onRetry={() => {
        setError(null);
        setIsLoading(true);
        // Re-trigger the useEffect by updating a dependency
      }}
      emptyMessage="No content found for this campaign"
      errorMessage={error}
    >
      <Grid container spacing={3}>
        {/* Content Summary Card */}
        <Grid item xs={12}>
          <Card elevation={0} variant="outlined">
            <CardHeader 
              title="Campaign Content" 
              action={
                canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateContent}
                  >
                    Create Content
                  </Button>
                )
              }
            />
            <Divider />
            
            {/* Filter Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                aria-label="content filter tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={`All (${contents.length})`} />
                <Tab label={`Draft (${filterContentByStatus('draft').length})`} />
                <Tab label={`Scheduled (${filterContentByStatus('scheduled').length})`} />
                <Tab label={`Published (${filterContentByStatus('published').length})`} />
              </Tabs>
            </Box>
            
            <CardContent sx={{ p: 0 }}>
              {/* Tab Content */}
              {tabValue === 0 && renderContentList(filterContentByStatus('all'))}
              {tabValue === 1 && renderContentList(filterContentByStatus('draft'))}
              {tabValue === 2 && renderContentList(filterContentByStatus('scheduled'))}
              {tabValue === 3 && renderContentList(filterContentByStatus('published'))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewContent}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        {canUpdate && (
          <MenuItem onClick={handleEditContent}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteContent}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography color="error">Delete</Typography>
          </MenuItem>
        )}
      </Menu>
    </DataStateHandler>
  );
};

export default CampaignContentTab;